import { createElement, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Dialog, fmtDate } from "@/ui";
import { PaginatedPrint } from "./PaginatedPrint";
import { RECOMMENDATION_LABEL } from "./vocabulary";
import summaryPrintStyles from "@/styles/print-summary.css?url";

export interface SavedRendition {
  rendition_html?: string | null;
  file_id: string;
  version_id: string;
  receipt_id: string;
  classification: string;
  printed_at?: string;
  exported_at?: string;
  export_attribution?: string;
}

// Saved packet/intake sections contain escaped text and the intake rounds table.
// Decode only the saved grammar into React nodes. Never insert HTML, install a permissive Trusted Types policy, or
// fetch live form values. A changed server grammar fails closed and must be reconciled with its saved version.
const decode = (text: string) => text.replace(/&(amp|lt|gt|quot|#x27|#39);/g, (_, entity: string) => ({ amp: "&", lt: "<", gt: ">", quot: '"', "#x27": "'", "#39": "'" })[entity]!);
export function savedSections(html: string | null | undefined): string[] {
  if (!html) return [];
  return [...html.matchAll(/<section class="sheet"><pre>([\s\S]*?)<\/pre>/g)].map((m) => decode(m[1]!));
}

// A saved matrix cell that is a stored value prints as its word: the recommendations from the design system's one
// mapping (B14 C5: Strong Hire · Hire · Consider Other Role · No Hire, and the earlier scale's word), and Submitted.
const SAVED_WORD: Readonly<Record<string, string>> = { ...RECOMMENDATION_LABEL, submitted: "Submitted" };

// Summary matrices and intake tables share a deliberately small tag grammar. Only the named
// class/scope attributes are admitted; scripts, links, handlers and styles never enter the document.
function savedMarkup(body: string): ReactNode[] {
  const root: { tag: string; props: Record<string, string>; children: ReactNode[] } = { tag: "root", props: {}, children: [] };
  const stack = [root];
  for (const rawToken of body.match(/<[^>]*>|[^<]+/g) ?? []) {
    const token = rawToken.replace(/scope=(row|col)(?=>| )/g, 'scope="$1"');
    const current = stack.at(-1)!;
    if (!token.startsWith("<")) { current.children.push(SAVED_WORD[token] ?? decode(token)); continue; }
    const close = token.match(/^<\/(h1|h2|h3|p|section|table|thead|tbody|tr|th|td|dl|dt|dd|div|span)>$/);
    if (close) {
      if (stack.length < 2 || current.tag !== close[1]) return [];
      stack.pop(); stack.at(-1)!.children.push(createElement(current.tag, { ...current.props, key: stack.at(-1)!.children.length }, ...current.children));
      continue;
    }
    const open = token.match(/^<(h1|h2|h3|p|section|table|thead|tbody|tr|th|td|dl|dt|dd|div|span)((?: (?:class="(?:low|rating|matrix-continuation|rating-ladder|rating-tone--[1-5]|score-circle|print-evidence)(?: (?:low|rating))*"|scope="(?:row|col)"))*)>$/);
    if (!open) return [];
    const props: Record<string, string> = {};
    for (const attr of open[2]!.matchAll(/(class|scope)="([^"]*)"/g)) props[attr[1] === "class" ? "className" : "scope"] = attr[2]!;
    stack.push({ tag: open[1]!, props, children: [] });
  }
  return stack.length === 1 ? root.children : [];
}

function savedSummaryBody(html: string | null | undefined): string | undefined {
  return html?.match(/<body><header>[\s\S]*?<\/header>(<h1>[\s\S]*?)<footer>/)?.[1];
}

function savedMatrix(html: string | null | undefined): ReactNode[] {
  const body = savedSummaryBody(html);
  return body ? savedMarkup(body) : [];
}

function printLines(text: string, structuralHeader: boolean): ReactNode {
  return <div className="print-section-body">{text.split("\n").map((line, i) => {
    const page = structuralHeader && i === 0 ? /^(— page .*? —)(.*)$/.exec(line) : null;
    if (page) return <h3 className="print-section-title" key={i}><span className="print-page-marker">{page[1]}</span><span className="print-section-name">{page[2]}</span></h3>;
    // His sections lead with a numbered red tile; the saved heading carries the number, the tile is how it prints.
    const numbered = structuralHeader && i === 0 ? /^== (\d{2}) · (.*) ==$/.exec(line) : null;
    if (numbered) return <h3 className="print-section-title" key={i}><span className="sequence-tile" aria-hidden="true">{numbered[1]}</span><span className="print-section-name">{numbered[2]}</span></h3>;
    if (structuralHeader && i === 0 && /^== .* ==$/.test(line)) return <h3 className="print-section-title" key={i}><span className="print-section-name">{line.replace(/^== | ==$/g, "")}</span></h3>;
    const field = /^([^:]{1,60}:)(\s.*)$/.exec(line);
    return <div className={`print-line${field ? " print-field" : ""}${/^\s*[▲◆]/.test(line) ? " print-flag" : ""}`} key={i}>{field ? <><span className="print-field__label">{field[1]}</span><span>{field[2]}</span></> : line || "\u00a0"}</div>;
  })}</div>;
}

// Intake page 8 interleaves escaped text with a typed rounds table. Validate every block and
// preserve its order; an unsupported block refuses the preview instead of silently losing a page.
function savedPages(html: string | null | undefined): ReactNode[] {
  const pages: ReactNode[] = [];
  for (const section of html?.matchAll(/<section class="sheet">([\s\S]*?)<\/section>/g) ?? []) {
    const body = section[1]!;
    const blocks = [...body.matchAll(/<pre>([\s\S]*?)<\/pre>|<table>[\s\S]*?<\/table>|<dl class="rating-ladder">[\s\S]*?<\/dl>|<p class="print-evidence">[\s\S]*?<\/p>/g)];
    if (!blocks.length || blocks.map((block) => block[0]).join("") !== body) return [];
    const content: ReactNode[] = [];
    for (const [index, block] of blocks.entries()) {
      if (block[1] !== undefined) content.push(<div key={index}>{printLines(decode(block[1]), index === 0)}</div>);
      else {
        const table = savedMarkup(block[0]);
        if (!table.length) return [];
        content.push(<div className={block[0].startsWith("<table>") ? "print-intake-rounds" : "print-typed-block"} key={index}>{table}</div>);
      }
    }
    pages.push(<section className="sheet print-page" key={pages.length}>{content}</section>);
  }
  return pages;
}

export function SavedPrint({ title, value, kind, onClose }: { title: string; value: SavedRendition | null; kind: "packet" | "intake" | "summary"; onClose: () => void }) {
  const printSource = useRef<HTMLElement>(null);
  const [printReady, setPrintReady] = useState(false);
  useEffect(() => {
    if (!value) return;
    document.body.classList.add("printing-export");
    return () => document.body.classList.remove("printing-export");
  }, [value]);
  if (!value) return null;
  const sections = savedSections(value.rendition_html);
  const matrix = kind === "summary" ? savedMatrix(value.rendition_html) : [];
  const pages = savedPages(value.rendition_html);
  const valid = pages.length > 0 || matrix.length > 0;
  const all = sections.join("\n");
  const lines = all.split("\n");
  const summaryBody = kind === "summary" ? savedSummaryBody(value.rendition_html) : undefined;
  const summaryHeading = summaryBody?.match(/^<h1>([^<]*)<\/h1>/)?.[1];
  const summaryHeader = [...(summaryBody?.split("<section")[0]?.matchAll(/<p>([^<]*)<\/p>/g) ?? [])].map(match => decode(match[1]!));
  const packetVersion = all.split("\n").find((line) => /^Packet .* · guide v/.test(line));
  // Separate identity from the entire status segment, preserving dates and paper confirmation.
  const packetMetadata = packetVersion?.match(/^(Packet .*? · guide v[^·]+) · (.*)$/);
  const stamp = packetMetadata?.[2] ?? lines.find(line => /^(Finalized |DRAFT — not finalized)/.test(line)) ?? summaryHeader.find(line => /^Sheet .+ · guide v/.test(line)) ?? "Status not recorded";
  // Every saved instrument heads itself "GoTo Foods | <SECTION>" (the intake's own spelling has no space yet).
  const brandLine = lines.find(line => /^GoTo ?Foods \| /.test(line));
  const firstHeading = summaryHeading ? decode(summaryHeading) : brandLine?.replace(/^GoTo ?Foods \| /, "");
  const packetIdentity = (packetMetadata?.[1] ?? packetVersion)?.trim();
  const running = [firstHeading, packetIdentity].filter(Boolean).join(" · ");
  // V1-INTERACTIONS §5.1: the instrument's boxed identity block, read back from the saved lines it was rendered from.
  const namedPair = (pattern: RegExp) => lines.map(line => pattern.exec(line)).find(Boolean);
  const who = kind === "packet" ? namedPair(/^Candidate: (.*) · Target role: (.*)$/) : undefined;
  const when = kind === "packet" ? namedPair(/^Interviewer: (.*) · Date: (.*)$/) : undefined;
  const identityFields = who && when ? JSON.stringify([
    { label: "Candidate name", value: who[1] }, { label: "Target role", value: who[2] },
    { label: "Interviewer", value: when[1] }, { label: "Date", value: when[2] },
  ]) : undefined;
  return createPortal(<Dialog title={title} open onClose={onClose}>
    {kind === "summary" && <link rel="stylesheet" href={summaryPrintStyles} media="print" />}
    <div className="no-print">
      <div className="acts"><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn--primary" disabled={!valid || !printReady} onClick={() => window.print()}>Print / Save as PDF</button></div>
      <p className="muted mono print-provenance">Version {value.version_id} · export receipt {value.receipt_id} · {fmtDate(value.printed_at ?? value.exported_at, true)}</p>
      {!valid && <p role="alert">This saved document cannot be previewed. Close and try again.</p>}
    </div>
    {valid && <article className={`export-doc export-doc--${kind}`} aria-label="Saved print rendition" ref={printSource}>
      <table className="print-frame" role="presentation"><thead><tr><td><header className="print-running">GoTo Foods | {running || title}<div className="print-stamp">{stamp}</div>{value.export_attribution && <div className="print-attribution">{value.export_attribution}</div>}<div className="print-revision mono">{value.version_id}</div></header></td></tr></thead><tbody><tr><td>
      {matrix.length > 0 && <div className="print-matrix">{matrix}</div>}
      {pages}
      </td></tr></tbody><tfoot><tr><td><footer className="print-footer">© GoTo Foods — Confidential, Internal Use Only</footer></td></tr></tfoot></table>
      <PaginatedPrint source={printSource} section={kind === "intake" ? "Recruiting Intake Form" : kind === "summary" ? "Interview Summary" : (firstHeading || "Interview Packet")} identity={running || title} fields={identityFields} stamp={stamp} attribution={value.export_attribution} revision={value.version_id} onReady={setPrintReady} />
    </article>}
  </Dialog>, document.body);
}
